import { TSmartAccountClient } from '@/types';
import { Hex, parseEventLogs } from 'viem';
import { getNonce, pimlicoClient } from './account-utils';
import {
  ContractType,
  ContractMethod,
  CONTRACT_ABI_MAP,
  CommentContractEvent,
  publicClient,
} from './contract-utils';
import { COMMENT_CONTRACT_ADDRESS } from './constants';
import { keccak256 } from 'viem';
import {
  updateCommentTxHashOnCommentV2,
  updateReplyTxHashV2,
} from '@/db/queries';

// user operation recipt takes long time to resolve hence the long timeout
const RECEIPT_WAIT_TIMEOUT = 30000; // 30 seconds

interface AddCommentTransactionArgs {
  portal: string;
  ddocId: string;
  contentHash: string;
}
interface ResolveCommentTransactionArgs {
  portal: string;
  ddocId: string;
  commentIndex: number;
  contentHash: string;
}
interface DeleteCommentTransactionArgs {
  portal: string;
  ddocId: string;
  commentIndex: number;
  contentHash: string;
}
interface AddReplyTransactionArgs {
  portal: string;
  ddocId: string;
  commentIndex: number;
  contentHash: string;
}
interface GetAllCommentsProps {
  portal: string;
  ddocId: string;
  resultsPerPage: number;
  page: number;
}
interface GetCommentCount {
  portal: string;
  ddocId: string;
}
interface GetReplyCount {
  portal: string;
  ddocId: string;
  commentIndex: number;
}
interface GetAllRepliesProps {
  portal: string;
  ddocId: string;
  commentIndex: number;
}
export interface OnchainComment {
  author: string;
  contentHash: string;
  timestamp: number;
  isResolved: boolean;
  isDeleted: boolean;
}
export interface OnchainReply {
  author: string;
  contentHash: string;
  timestamp: number;
  isDeleted: boolean;
}

type ResolvedContent = {
  id: string;
  username: string;
  encryptedData: string;
  timestamp: number;
};

export type Reply = {
  _id: string;
  contentHash: string;
  author: string;
  replyIndex: number;
  resolvedContent: ResolvedContent;
  isDeleted: boolean;
  timestamp: string;
};

type Comment = {
  _id: string;
  portal: string;
  ddocId: string;
  commentIndex: number;
  author: string;
  contentHash: string;
  resolvedContent: ResolvedContent;
  isDeleted: boolean;
  timestamp: string;
  replies: Reply[];
  isResolved: boolean;
  __v: number;
};

export type CommentsArray = Comment[];

export const addCommentTransaction = async (
  smartAccountClient: TSmartAccountClient,
  args: AddCommentTransactionArgs,
  commentId: string
) => {
  const txHash = await smartAccountClient.sendUserOperation({
    calls: [
      {
        abi: CONTRACT_ABI_MAP[ContractType.Comment],
        functionName: ContractMethod.AddComment,
        args: [args.portal, args.ddocId, args.contentHash],
        to: COMMENT_CONTRACT_ADDRESS,
      },
    ],
    nonce: getNonce(),
    account: smartAccountClient.account,
  });
  updateCommentTxHashOnCommentV2(args.ddocId, commentId, txHash);

  const { logs } = await pimlicoClient.waitForUserOperationReceipt({
    hash: txHash,
    timeout: RECEIPT_WAIT_TIMEOUT,
  });

  const parsedLog = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    eventName: CommentContractEvent.CommentAdded,
    logs,
  });

  if (parsedLog.length === 0) throw new Error('No Comments Added Event Found!');
  //@ts-ignore
  const commentIndex = parsedLog[0]?.args?.commentIndex;
  //@ts-ignore
  const contentHash = parsedLog[0]?.args?.contentHash;
  return { commentIndex, contentHash };
};

export const resolveCommentTransaction = async (
  smartAccountClient: TSmartAccountClient,
  args: ResolveCommentTransactionArgs
): Promise<{ commentIndex: BigInt; contentHash: string; ddocId: string }> => {
  const txHash = await smartAccountClient.sendUserOperation({
    calls: [
      {
        abi: CONTRACT_ABI_MAP[ContractType.Comment],
        functionName: ContractMethod.ResolveComment,
        args: [args.portal, args.ddocId, args.commentIndex, args.contentHash],
        to: COMMENT_CONTRACT_ADDRESS,
      },
    ],
    account: smartAccountClient.account,
  });
  const { logs } = await pimlicoClient.waitForUserOperationReceipt({
    hash: txHash,
    timeout: RECEIPT_WAIT_TIMEOUT,
  });

  const parsedLog = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    eventName: CommentContractEvent.CommentResolved,
    logs,
  });

  if (parsedLog.length === 0)
    throw new Error('No Comment Resolved Event Found!');
  //@ts-ignore
  const commentIndex = parsedLog[0]?.args?.commentIndex;
  return {
    commentIndex,
    //@ts-ignore
    contentHash: parsedLog[0]?.args._contentHash,
    //@ts-ignore
    ddocId: parsedLog[0]?.args.ddocId,
  };
};

export const unresolveCommentTransaction = async (
  smartAccountClient: TSmartAccountClient,
  args: ResolveCommentTransactionArgs
): Promise<{ commentIndex: BigInt; contentHash: string; ddocId: string }> => {
  const txHash = await smartAccountClient.sendUserOperation({
    calls: [
      {
        abi: CONTRACT_ABI_MAP[ContractType.Comment],
        functionName: ContractMethod.UnresolveComment,
        args: [args.portal, args.ddocId, args.commentIndex, args.contentHash],
        to: COMMENT_CONTRACT_ADDRESS,
      },
    ],
    nonce: getNonce(),
    account: smartAccountClient.account,
  });

  const { logs } = await pimlicoClient.waitForUserOperationReceipt({
    hash: txHash,
    timeout: RECEIPT_WAIT_TIMEOUT,
  });

  const parsedLog = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    eventName: CommentContractEvent.CommentUnresolved,
    logs,
  });

  if (parsedLog.length === 0)
    throw new Error('No Comment Unresolved Event Found!');
  //@ts-ignore
  const commentIndex = parsedLog[0]?.args?.commentIndex;

  return {
    commentIndex,
    //@ts-ignore
    contentHash: parsedLog[0]?.args._contentHash,
    //@ts-ignore
    ddocId: parsedLog[0]?.args.ddocId,
  };
};

export const deleteCommentTransaction = async (
  smartAccountClient: TSmartAccountClient,
  args: DeleteCommentTransactionArgs
): Promise<{ commentIndex: BigInt; contentHash: string; ddocId: string }> => {
  const txHash = await smartAccountClient.sendUserOperation({
    calls: [
      {
        abi: CONTRACT_ABI_MAP[ContractType.Comment],
        functionName: ContractMethod.DeleteComment,
        args: [args.portal, args.ddocId, args.commentIndex, args.contentHash],
        to: COMMENT_CONTRACT_ADDRESS,
      },
    ],
    account: smartAccountClient.account,
  });

  const { logs } = await pimlicoClient.waitForUserOperationReceipt({
    hash: txHash,
    timeout: RECEIPT_WAIT_TIMEOUT,
  });

  const parsedLog = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    eventName: CommentContractEvent.CommentDeleted,
    logs,
  });

  if (parsedLog.length === 0)
    throw new Error('No Comment Deleted Event Found!');
  //@ts-ignore
  const commentIndex = parsedLog[0]?.args?.commentIndex;

  return {
    commentIndex,
    //@ts-ignore
    contentHash: parsedLog[0]?.args._contentHash,
    //@ts-ignore
    ddocId: parsedLog[0]?.args.ddocId,
  };
};

export const addReplyTransaction = async (
  smartAccountClient: TSmartAccountClient,
  args: AddReplyTransactionArgs,
  commentId: string,
  replyId: string
) => {
  const txHash = await smartAccountClient.sendUserOperation({
    calls: [
      {
        abi: CONTRACT_ABI_MAP[ContractType.Comment],
        functionName: ContractMethod.AddReply,
        args: [args.portal, args.ddocId, args.commentIndex, args.contentHash],
        to: COMMENT_CONTRACT_ADDRESS,
      },
    ],
    account: smartAccountClient.account,
  });
  updateReplyTxHashV2(args.ddocId, commentId, replyId, txHash);

  const { logs } = await pimlicoClient.waitForUserOperationReceipt({
    hash: txHash,
    timeout: RECEIPT_WAIT_TIMEOUT,
  });

  const parsedLog = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    eventName: CommentContractEvent.ReplyAdded,
    logs,
  });

  if (parsedLog.length === 0) throw new Error('No Reply Added Event Found!');
  //@ts-ignore
  const replyIndex = parsedLog[0]?.args?.replyIndex;
  return replyIndex;
};

export const getAllComments = async ({
  portal,
  ddocId,
  resultsPerPage,
  page,
}: GetAllCommentsProps) => {
  const comments = await publicClient.readContract({
    address: COMMENT_CONTRACT_ADDRESS,
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    functionName: ContractMethod.GetComments,
    args: [portal, ddocId, resultsPerPage, page],
  });
  if (!comments) throw new Error('Failed to fetch comments');

  return comments as OnchainComment[];
};

export const getCommentCount = async ({ portal, ddocId }: GetCommentCount) => {
  const _commentCount = await publicClient.readContract({
    address: COMMENT_CONTRACT_ADDRESS,
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    functionName: ContractMethod.GetCommentsCount,
    args: [portal, ddocId],
  });
  const commentCount = Number(_commentCount);
  return commentCount;
};

export const getReplyCount = async ({
  portal,
  ddocId,
  commentIndex,
}: GetReplyCount) => {
  if (!commentIndex?.toString()) return 0;
  const _replyCount = await publicClient.readContract({
    address: COMMENT_CONTRACT_ADDRESS,
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    functionName: ContractMethod.GetReplyCount,
    args: [portal, ddocId, commentIndex],
  });
  const replyCount = Number(_replyCount);
  return replyCount;
};

export const getAllReplies = async ({
  portal,
  ddocId,
  commentIndex,
}: GetAllRepliesProps) => {
  const comments = await publicClient.readContract({
    address: COMMENT_CONTRACT_ADDRESS,
    abi: CONTRACT_ABI_MAP[ContractType.Comment],
    functionName: ContractMethod.GetReplies,
    args: [portal, ddocId, commentIndex],
  });
  if (!comments) throw new Error('Failed to fetch replies');

  return comments as OnchainReply[];
};

export const getCommentsDataById = async (
  ddocId: string
): Promise<CommentsArray> => {
  const url = `${process.env.NEXT_PUBLIC_COMMENTS_INDEXER}/comments/${keccak256(ddocId as Hex)}`;

  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`HTTP error! status: ${res.status}`);
  }
  const jsonResponse = (await res.json()) as any;
  return jsonResponse;
};
export const removeDuplicatesRepliesByLatest = (
  array: any /** TODO use proper replies type*/
) => {
  array.sort((a: any, b: any) => a.createdAt.getTime() - b.createdAt.getTime());
  const uniqueMap = new Map();
  for (const obj of array) {
    if (!uniqueMap.has(obj.content)) {
      uniqueMap.set(obj.content, obj);
    }
  }
  return Array.from(uniqueMap.values());
};
export const checkIfReplyTxSuccessful = async (txHash: Hex) => {
  try {
    const { logs } = await pimlicoClient.waitForUserOperationReceipt({
      hash: txHash,
      timeout: RECEIPT_WAIT_TIMEOUT,
    });

    const parsedLog = parseEventLogs({
      abi: CONTRACT_ABI_MAP[ContractType.Comment],
      eventName: CommentContractEvent.ReplyAdded,
      logs,
    });

    if (parsedLog.length === 0) throw new Error('No Reply Added Event Found!');
    //@ts-ignore
    const replyIndex = parsedLog[0]?.args?.replyIndex;
    return { status: true, replyIndex: Number(replyIndex) };
  } catch (error: any) {
    console.log(error);
    return { status: false, replyIndex: undefined };
  }
};
export const checkIfCommentTxSuccessful = async (txHash: Hex) => {
  try {
    const { logs } = await pimlicoClient.waitForUserOperationReceipt({
      hash: txHash,
      timeout: RECEIPT_WAIT_TIMEOUT,
    });

    const parsedLog = parseEventLogs({
      abi: CONTRACT_ABI_MAP[ContractType.Comment],
      eventName: CommentContractEvent.CommentAdded,
      logs,
    });

    if (parsedLog.length === 0)
      throw new Error('No Comment Added Event Found!');
    //@ts-ignore
    const commentIndex = parsedLog[0]?.args?.commentIndex;
    return { status: true, commentIndex: Number(commentIndex) };
  } catch (error: any) {
    console.log(error);
    return { status: false, commentIndex: undefined };
  }
};
